import { z } from 'zod'
import { BotMode } from '../../enums/bot-mode.enum'
import { BotPeakStrategy } from '../../enums/bot-peak-strategy.enum'
import { BotMaType } from '../../enums/bot-ma-type.enum'
import { BotPreset } from '../../enums/bot-preset.enum'
import { refineMinMax } from './bot-refine'
import {
  factorWeightsSchema,
  DEFAULT_FACTOR_WEIGHTS,
} from '../../investments/dip-pipeline/dip-factors.schema'

export const botAccountSettingsSchema = z
  .object({
    id: z.string().guid(),
    brokerAccountId: z.string().guid(),
    isEnabled: z.boolean().default(false),
    mode: z.nativeEnum(BotMode).default(BotMode.AUTO_BUY),
    dropPercentMin: z.number().min(0).max(100).default(1.0),
    dropPercentMax: z.number().min(0).max(100).default(3.0),
    shiftPercentMin: z.number().min(0).max(100).default(0.5),
    shiftPercentMax: z.number().min(0).max(100).default(1.5),
    confirmationTicks: z.number().int().min(1).default(3),
    confirmationSeconds: z.number().int().min(0).default(30),
    lotsPerStep: z.number().int().min(1).default(1),
    maxLotsPerBuy: z.number().int().min(1).default(1),
    // within-day accumulation ramp: each successive buy escalates the shift gap and the lot
    // size by these multiples (0 = off, i.e. the static ladder). see the design spec.
    shiftChange: z.number().min(0).default(0),
    lotChange: z.number().min(0).default(0),
    anchorStaleDays: z.number().int().min(0).max(365).default(3),
    peakStrategy: z.nativeEnum(BotPeakStrategy).default(BotPeakStrategy.RATCHET),
    peakLookbackDays: z.number().int().min(1).max(365).default(20),
    maType: z.nativeEnum(BotMaType).default(BotMaType.SMA),
    preset: z.nativeEnum(BotPreset).nullable().default(null),
    factorWeights: factorWeightsSchema.default(DEFAULT_FACTOR_WEIGHTS),
    moodRate: z.number().default(0),
    allowBuyAboveTarget: z.boolean().default(false),
    // user-set spendable budget; the bot decrements it (cost + commission) per fill, so the
    // stored value may go negative — no min here.
    freeAmount: z.number().default(0),
    // minimum cash balance; the bot fires a BOT_LOW_BALANCE notification when account cash drops below this (0 = disabled)
    lowBalanceThreshold: z.number().min(0).default(0),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
  })
  .superRefine((d, ctx) => {
    refineMinMax(
      ctx,
      d,
      'dropPercentMin',
      'dropPercentMax',
      'dropPercentMin must be <= dropPercentMax'
    )
    refineMinMax(
      ctx,
      d,
      'shiftPercentMin',
      'shiftPercentMax',
      'shiftPercentMin must be <= shiftPercentMax'
    )
  })

export type BotAccountSettingsDto = z.infer<typeof botAccountSettingsSchema>

export const updateBotAccountSettingsSchema = z
  .object({
    mode: z.nativeEnum(BotMode).optional(),
    dropPercentMin: z.number().min(0).max(100).optional(),
    dropPercentMax: z.number().min(0).max(100).optional(),
    shiftPercentMin: z.number().min(0).max(100).optional(),
    shiftPercentMax: z.number().min(0).max(100).optional(),
    confirmationTicks: z.number().int().min(1).optional(),
    confirmationSeconds: z.number().int().min(0).optional(),
    lotsPerStep: z.number().int().min(1).optional(),
    maxLotsPerBuy: z.number().int().min(1).optional(),
    shiftChange: z.number().min(0).optional(),
    lotChange: z.number().min(0).optional(),
    anchorStaleDays: z.number().int().min(0).max(365).optional(),
    peakStrategy: z.nativeEnum(BotPeakStrategy).optional(),
    peakLookbackDays: z.number().int().min(1).max(365).optional(),
    maType: z.nativeEnum(BotMaType).optional(),
    preset: z.nativeEnum(BotPreset).nullable().optional(),
    factorWeights: factorWeightsSchema.optional(),
    moodRate: z.number().optional(),
    allowBuyAboveTarget: z.boolean().optional(),
    freeAmount: z.number().optional(),
    lowBalanceThreshold: z.number().min(0).optional(),
  })
  .superRefine((d, ctx) => {
    refineMinMax(
      ctx,
      d,
      'dropPercentMin',
      'dropPercentMax',
      'dropPercentMin must be <= dropPercentMax'
    )
    refineMinMax(
      ctx,
      d,
      'shiftPercentMin',
      'shiftPercentMax',
      'shiftPercentMin must be <= shiftPercentMax'
    )
  })

export type UpdateBotAccountSettingsDto = z.infer<typeof updateBotAccountSettingsSchema>
